import { AccessTime, Add, CalendarMonth, Delete, EditOutlined, ExpandMore, LocationOn, PeopleOutlined, WarningAmberOutlined, Work } from "@mui/icons-material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { Autocomplete, Box, Button, Divider, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Skeleton, Switch, TextField, Tooltip, Typography } from "@mui/material"
import { deleteHoliday, getInvoicePreference, getStandardHoursSchedule, insertOrganizationpreference, updateGeoFencePreferences, updateWeekendData } from "api/services/preferencess";
import { snack } from "components/toast";
import { useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getHoursOptionsForSWH, getMinutesOptions } from "utils";
import { assignShiftUsers, getShifts, markShiftAsDefault } from "api/services/shift";
import { getUsersActiveAndInactive } from "api/services/users";
import { UserProfileContext } from "context/UserProfile";
import moment from "moment";
import AddHoliday from "./addHoliday";
import ShiftDialog from "./shiftDialog";
import Table from "components/Table";
import { useConfirm } from "context/ConfirmDialog";
import CampaignIcon from "@mui/icons-material/Campaign";
import AttendanceInfo from "./attendanceInfo";
import Announcement from "./../../../assets/images/Announcement.svg";
import DialogWrapper from "components/DialogWrapper";
import LoadingButton from "components/LoadingButton";

type GeoFenceLocation = {
    name: string;
    latitude: string;
    longitude: string;
    radiusMeters: string;
};

type GeoFencePreferences = {
    enabled: boolean;
    radiusMeters: string;
    locations: GeoFenceLocation[];
};

type GeoFenceLocationErrors = Partial<Record<keyof GeoFenceLocation, string>>;

const emptyGeoFenceLocation = (): GeoFenceLocation => ({
    name: "",
    latitude: "",
    longitude: "",
    radiusMeters: "",
});

const defaultGeoFencePreferences = (): GeoFencePreferences => ({
    enabled: false,
    radiusMeters: "100",
    locations: [emptyGeoFenceLocation()],
});

const getGeoFenceHelperText = (message?: string) => message === "Mandatory field" ? "" : message;

const WEEKEND_OPTIONS = [
    { label: "Saturday", value: "saturday" },
    { label: "Sunday", value: "sunday" },
    { label: "First Saturday", value: "first-saturday" },
    { label: "Second Saturday", value: "second-saturday" },
    { label: "Third Saturday", value: "third-saturday" },
    { label: "Fourth Saturday", value: "fourth-saturday" },
    { label: "Fifth Saturday", value: "fifth-saturday" },
];

function getShiftIcon(shift: any): { emoji: string; bg: string } {
    const name = (shift.name || "").toLowerCase();
    const hour = shift.inTime ? parseInt(shift.inTime.split(":")[0], 10) : 9;
    if (name.includes("night") || hour >= 20 || hour < 5)
        return { emoji: "🌙", bg: "#ede7f6" };
    if (name.includes("morning") || (hour >= 5 && hour < 12))
        return { emoji: "☀️", bg: "#fff8e1" };
    if (name.includes("afternoon") || (hour >= 12 && hour < 17))
        return { emoji: "🌤️", bg: "#e3f2fd" };
    if (name.includes("evening") || hour >= 17)
        return { emoji: "🌆", bg: "#fce4ec" };
    return { emoji: "🕐", bg: "#f5f5f5" };
}

// Shift duration in minutes, handling a shift that crosses midnight (e.g. a night shift
// 22:00 -> 06:00) the same way it's saved: outTime numerically before inTime means next day.
function getShiftDurationMinutes(shift: any): number {
    const toMins = (t: string) => {
        if (!t) return 0;
        const [h, m] = t.split(":").map(Number);
        return h * 60 + (m || 0);
    };
    const inMins = toMins(shift.inTime);
    const outMins = toMins(shift.outTime);
    return outMins <= inMins ? outMins + 24 * 60 - inMins : outMins - inMins;
}

const formatMinutesDuration = (mins: number) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

function AttendancePreferences() {
    const queryClient = useQueryClient();
    var currentYearString = moment().format('YYYY');
    var currentYearInt = parseInt(currentYearString, 10);
    const yearsArray = Array.from({ length: currentYearInt - 2009 + 1 }, (_, i) => 2010 + i);
    const sortedYearsArray = yearsArray.sort((a, b) => b - a);
    const [state, setState]: any = useState([]);
    const [weekendData, setWeekendData] = useState<any[]>([]);
    const [hours, setHours] = useState<any>(null);
    const [minutes, setMinutes] = useState<any>(null);
    const [weekendExpanded, setWeekendExpanded] = useState<boolean>(false);
    const [holidaysExpanded, setHolidaysExpanded] = useState<boolean>(false);
    const [geoFenceExpanded, setGeoFenceExpanded] = useState<boolean>(false);
    const [geoPreferences, setGeoPreferences] = useState<GeoFencePreferences>(defaultGeoFencePreferences());
    const [geoOpenInfo, setGeoOpenInfo] = useState(false);
    const [fetchingLocationIndex, setFetchingLocationIndex] = useState<number | null>(null);
    const [defaultRadiusError, setDefaultRadiusError] = useState("");
    const [locationErrors, setLocationErrors] = useState<Record<number, GeoFenceLocationErrors>>({});
    const [isEditingStdHours, setIsEditingStdHours] = useState(false);
    const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
    const [editingShift, setEditingShift] = useState<any>(null);
    const [showAllShifts, setShowAllShifts] = useState(false);
    const [assigningShiftId, setAssigningShiftId] = useState<number | null>(null);
    const [assignUsers, setAssignUsers] = useState<any[]>([]);
    const { data: uData } = useContext(UserProfileContext);
    const orgId = uData?.organization?.id;
    const { data: shiftsData, isLoading: isShiftsLoading } = useQuery(["shifts", orgId], getShifts, { enabled: !!orgId });
    // Standard Working Hours are effective-dated: what applies today, plus a saved change
    // waiting to take effect (from tomorrow).
    const { data: stdScheduleRes } = useQuery("standard-hours-schedule", getStandardHoursSchedule);
    const stdSchedule = (stdScheduleRes as any)?.data;
    const shifts: any[] = shiftsData?.data || [];
    const { data: orgUsersData } = useQuery("users", getUsersActiveAndInactive);
    const orgUsers: any[] = orgUsersData?.data || [];
    const { mutate: setDefaultShift, isLoading: isSettingDefault } = useMutation(
        (id: number) => markShiftAsDefault(id),
        {
            onSuccess: () => {
                snack.success("Default shift updated. This Shift will be applicable from Next day");
                queryClient.invalidateQueries("shifts");
            },
            onError: (err: any) => {
                snack.error(err?.response?.data?.message || "Failed to update default shift");
            },
        }
    );
    const { mutate: saveShiftUsers, isLoading: isSavingShiftUsers } = useMutation(assignShiftUsers, {
        onSuccess: () => {
            snack.success("Users assigned successfully");
            queryClient.invalidateQueries("shifts");
            setAssigningShiftId(null);
        },
        onError: (err: any) => {
            snack.error(err?.response?.data?.message || "Failed to assign users");
        },
    });
    const [AddOpenHoliday, setAddOpenHoliday] = useState<boolean>(false);
    const [years, setyears] = useState<any>(sortedYearsArray);
    const [year, setYear] = useState<any>(currentYearInt);
    const [holidays, setHolidays] = useState<any>([]);
    const [totalHolidays, setTotalHolidays] = useState<any>([]);
    const [isMarkAbsent, setIsMarkAbsent] = useState<any>("NO");
    const [isFaceVerificationEnabled, setIsFaceVerificationEnabled] = useState<any>("NO");
    const [openInfo, setOpenInfo] = useState(false);
    const confirm = useConfirm()
    const geoPreferencesRef = useRef(geoPreferences);
    useEffect(() => { geoPreferencesRef.current = geoPreferences; }, [geoPreferences]);
    const radiusSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { data: invoicePreferences, isLoading: isInvoicePreferencesLoading } = useQuery(
        "attendance-preferences",
        getInvoicePreference,
        {
            onSuccess: (res: any) => {
                res?.data?.holidayPreferences?.addholiday.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
                setTotalHolidays(res?.data?.holidayPreferences?.addholiday);
                const Yearholidays = res?.data?.holidayPreferences?.addholiday.filter((item: any) => moment(item?.date).format("YYYY") === String(year))
                setHolidays(Yearholidays);
                setWeekendData(res?.data?.holidayPreferences?.updateweekend);
                setHours(res.data.holidayPreferences.updateovertime?.hours);
                setMinutes(res.data.holidayPreferences.updateovertime?.minutes);
                setIsMarkAbsent(res?.data?.holidayPreferences?.isMarkAbsent || "NO");
                setIsFaceVerificationEnabled(res?.data?.holidayPreferences?.faceVerification || "NO");
                const geo = res?.data?.geoFencePreferences || {};
                const geoLocations = Array.isArray(geo?.locations) && geo.locations.length
                    ? geo.locations
                    : [emptyGeoFenceLocation()];
                setGeoPreferences({
                    enabled: geo?.enabled === true || geo?.enabled === "true",
                    radiusMeters: String(geo?.radiusMeters || "100"),
                    locations: geoLocations.map((loc: any) => ({
                        name: loc?.name || "",
                        latitude: loc?.latitude != null ? String(loc.latitude) : "",
                        longitude: loc?.longitude != null ? String(loc.longitude) : "",
                        radiusMeters: loc?.radiusMeters != null ? String(loc.radiusMeters) : "",
                    })),
                });
            },
        }
    );
    useEffect(() => {
        const Yearholidays = totalHolidays.filter((item: any) => moment(item?.date).format("YYYY") === String(year))
        setHolidays(Yearholidays);
    }, [year])

    const onChange = (e: any) => {
        setYear(e.target.value);
    }

    const { mutate, isLoading: insertLoad } = useMutation(updateWeekendData, {
        onSuccess: () => {
            snack.success("Preferences Updated");
            queryClient.invalidateQueries("attendance-preferences");
        },
        onError: (err: any) => {
            snack.error(err);
        }
    });

    const handleWeekendToggle = (opt: { label: string; value: string }) => {
        const isSelected = weekendData?.some((d: any) => d.value === opt.value);
        const newData: any[] = isSelected
            ? weekendData.filter((d: any) => d.value !== opt.value)
            : [...weekendData, opt];
        setWeekendData(newData);
        mutate({ data: { typeofdata: "updateweekend", data: newData } });
    };

    const { mutate: deleteHolidayMutate } = useMutation(deleteHoliday, {
        onSuccess: (res: any) => {
            const all = res?.data?.holidayPreferences?.addholiday || [];
            setTotalHolidays(all);
            setHolidays(all.filter((item: any) => moment(item?.date).format("YYYY") === String(year)));
            snack.success("Holiday Deleted");
        },
        onError: (err: any) => {
            snack.error(err.response?.data?.message || "Failed to delete");
        },
    });

    const handleDeleteHoliday = (row: any) => {
        confirm({
            msg: "Are you sure you want to delete this Holiday?",
            action: () => deleteHolidayMutate(row),
        });
    };

    const { mutate: saveGeoFence, isLoading: isSavingGeoFence } = useMutation(updateGeoFencePreferences, {
        onSuccess: () => {
            snack.success("Geo Fence Preferences Updated");
            queryClient.invalidateQueries("attendance-preferences");
        },
        onError: (err: any) => {
            snack.error(err?.response?.data?.message || "Failed to update geo fence preferences");
        },
    });

    const updateGeoLocation = (index: number, field: keyof GeoFenceLocation, value: string) => {
        setGeoPreferences((prev) => ({
            ...prev,
            locations: prev.locations.map((loc, i) => i === index ? { ...loc, [field]: value } : loc),
        }));
        setLocationErrors((prev) => ({ ...prev, [index]: { ...prev[index], [field]: "" } }));
    };

    const handleAddGeoLocation = () => {
        setGeoPreferences((prev) => ({ ...prev, locations: [...prev.locations, emptyGeoFenceLocation()] }));
    };

    const handleRemoveGeoLocation = (index: number) => {
        setGeoPreferences((prev) => ({
            ...prev,
            locations: prev.locations.length === 1
                ? [emptyGeoFenceLocation()]
                : prev.locations.filter((_, i) => i !== index),
        }));
    };

    const handleUseCurrentGeoLocation = (index: number) => {
        if (!navigator.geolocation) { snack.error("Geolocation is not supported in this browser"); return; }
        setFetchingLocationIndex(index);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGeoPreferences((prev) => ({
                    ...prev,
                    locations: prev.locations.map((loc, i) =>
                        i === index ? { ...loc, latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude) } : loc
                    ),
                }));
                setFetchingLocationIndex(null);
            },
            (err) => { setFetchingLocationIndex(null); snack.error("Location error: " + (err?.message || "Unable to get location")); },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const autoSaveGeoFence = (overrides: Partial<GeoFencePreferences> = {}) => {
        const prefs = { ...geoPreferencesRef.current, ...overrides };
        const radiusMeters = Number(prefs.radiusMeters);
        if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return;
        const locs = prefs.locations
            .filter((loc) => loc.name.trim() && String(loc.latitude).trim() && String(loc.longitude).trim())
            .map((loc) => ({
                name: loc.name.trim(),
                latitude: Number(loc.latitude),
                longitude: Number(loc.longitude),
                radiusMeters: loc.radiusMeters ? Number(loc.radiusMeters) : radiusMeters,
            }));
        saveGeoFence({ data: { geoFencePreferences: { enabled: prefs.enabled, radiusMeters, locations: locs } } });
    };

    const handleSaveGeoFence = () => {
        const radiusMeters = Number(geoPreferences.radiusMeters);
        const nextLocationErrors: Record<number, GeoFenceLocationErrors> = {};
        let nextDefaultRadiusError = "";
        if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) nextDefaultRadiusError = "Mandatory field";
        geoPreferences.locations.forEach((loc, index) => {
            const rowErrors: GeoFenceLocationErrors = {};
            const lat = String(loc.latitude ?? "").trim();
            const lng = String(loc.longitude ?? "").trim();
            const rad = String(loc.radiusMeters ?? "").trim();
            const latNum = Number(lat); const lngNum = Number(lng);
            if (!loc.name.trim()) rowErrors.name = "Mandatory field";
            if (!lat) rowErrors.latitude = "Mandatory field";
            else if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) rowErrors.latitude = "Invalid latitude";
            if (!lng) rowErrors.longitude = "Mandatory field";
            else if (!Number.isFinite(lngNum) || lngNum < -180 || lngNum > 180) rowErrors.longitude = "Invalid longitude";
            if (rad && (!Number.isFinite(Number(rad)) || Number(rad) <= 0)) rowErrors.radiusMeters = "Invalid radius";
            if (latNum === 0 && lngNum === 0) {
                rowErrors.latitude = rowErrors.latitude || "Invalid location";
                rowErrors.longitude = rowErrors.longitude || "Invalid location";
            }
            if (Object.keys(rowErrors).length) nextLocationErrors[index] = rowErrors;
        });
        if (geoPreferences.enabled && (nextDefaultRadiusError || Object.keys(nextLocationErrors).length)) {
            setDefaultRadiusError(nextDefaultRadiusError);
            setLocationErrors(nextLocationErrors);
            snack.error("Please fill all mandatory geo fence fields");
            return;
        }
        setDefaultRadiusError(""); setLocationErrors({});
        const locs = geoPreferences.locations
            .filter((loc) => loc.name.trim() && String(loc.latitude).trim() && String(loc.longitude).trim())
            .map((loc) => ({
                name: loc.name.trim(),
                latitude: Number(loc.latitude),
                longitude: Number(loc.longitude),
                radiusMeters: loc.radiusMeters ? Number(loc.radiusMeters) : radiusMeters,
            }));
        if (geoPreferences.enabled && !locs.length) { snack.error("Please add at least one geo fence location"); return; }
        saveGeoFence({ data: { geoFencePreferences: { enabled: geoPreferences.enabled, radiusMeters, locations: locs } } });
    };

    const fmtDuration = (h: any, m: any) => (h && m ? `${h.label}h ${m.label}m` : "-");

    // Shifts don't auto-update when Standard Working Hours changes, so flag whichever ones
    // have drifted out of sync instead of silently letting the mismatch go unnoticed.
    const stdMinutesTotal = hours && minutes ? (parseInt(hours.value) || 0) * 60 + (parseInt(minutes.value) || 0) : null;
    const mismatchedShifts = stdMinutesTotal != null
        ? shifts.filter((s) => getShiftDurationMinutes(s) !== stdMinutesTotal)
        : [];

    const handleSaveStdHours = () => {
        if (!hours || !minutes) { snack.error("Please select hours and minutes"); return; }
        const totalMinutes = (parseInt(hours.value) || 0) * 60 + (parseInt(minutes.value) || 0);
        if (totalMinutes > 12 * 60) { snack.error("Standard Working Hours cannot be greater than 12 hours"); return; }
        const effectiveFrom = moment().add(1, "day").format("DD-MM-YYYY");
        confirm({
            msg: `The new Standard Working Hours will apply from tomorrow (${effectiveFrom}). Attendance up to today keeps the current value in every calculation and report.`,
            action: () => {
                mutate(
                    { data: { typeofdata: "updateovertime", data: { hours, minutes } } },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries("standard-hours-schedule");
                            snack.info(`Standard Working Hours will apply from ${effectiveFrom}. Check the Work Shifts section below — any shift whose timing no longer matches is flagged with a warning icon (shift timing changes also apply from the next day).`);
                        },
                    }
                );
                setIsEditingStdHours(false);
            },
        });
    };

    const handleUpdateIsAbsent = (newValue: string) => {
        confirm({
            msg: "Are you sure, Employees Attendance should be marked as Absent",
            action: () => mutate({ data: { typeofdata: "isMarkAbsent", data: { isMarkAbsent: newValue } } }),
        });
    }

    const handleUpdateFaceVerification = (newValue: string) => {
        confirm({
            msg: newValue === "YES"
                ? "Employees will need to pass a live face verification check (matched against their profile picture) every time they check in or out. Continue?"
                : "Face verification will no longer be required for check-in / check-out. Continue?",
            action: () => mutate({ data: { typeofdata: "faceVerification", data: { faceVerification: newValue } } }),
        });
    }

    return (
        <Box sx={{ p: 3, pb: 0 }}>
            <Grid container mt={2} gap={2}>
                
                <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #cfcbd6", borderRadius: "10px", ml: 1, overflow: "hidden" }}>
                        {/* Header */}
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, cursor: "pointer", userSelect: "none" }}
                            onClick={() => setWeekendExpanded((prev) => !prev)}
                        >
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#e8eaf6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <CalendarMonth sx={{ color: "#3949ab", fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography fontWeight={600} fontSize={15}>Weekend days</Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        {weekendData?.length
                                            ? weekendData.map((d: any) => d.label).join(", ") + " selected"
                                            : "No days selected"}
                                    </Typography>
                                </Box>
                            </Box>
                            <ExpandMore sx={{ color: "text.secondary", transform: weekendExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
                        </Box>

                        {/* Expanded: show all options as pills */}
                        {weekendExpanded && (
                            <Box px={2} pb={2} display="flex" gap={1} flexWrap="wrap">
                                {WEEKEND_OPTIONS.map((opt) => {
                                    const isSelected = weekendData?.some((d: any) => d.value === opt.value);
                                    return (
                                        <Box
                                            key={opt.value}
                                            px="14px"
                                            py="6px"
                                            borderRadius={2}
                                            onClick={() => handleWeekendToggle(opt)}
                                            sx={{
                                                cursor: "pointer",
                                                bgcolor: isSelected ? "primary.main" : "transparent",
                                                border: "1px solid",
                                                borderColor: isSelected ? "primary.main" : "rgba(0,0,0,0.15)",
                                                "&:hover": { opacity: 0.8 },
                                            }}
                                        >
                                            <Typography variant="caption" fontWeight={600} sx={{ color: isSelected ? "#fff" : "text.secondary" }}>
                                                {opt.label}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Grid>
                
                <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #cfcbd6", borderRadius: "10px", ml: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#fce4ec", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                                    🚨
                                </Box>
                                <Box>
                                    <Typography fontWeight={600} fontSize={15}>Mark Absent ?</Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        Employees who fail to record their Attendance should be marked as Absent ?
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                checked={isMarkAbsent === "YES"}
                                onChange={(e) => {
                                    const newVal = e.target.checked ? "YES" : "NO";
                                    setIsMarkAbsent(newVal);
                                    handleUpdateIsAbsent(newVal);
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #cfcbd6", borderRadius: "10px", ml: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#e0f2f1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                                    🪪
                                </Box>
                                <Box>
                                    <Typography fontWeight={600} fontSize={15}>Face Verification for Check-In / Check-Out</Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        Require employees to capture a live photo with a face detected before their Check-In or Check-Out is accepted.
                                    </Typography>
                                    <Typography fontSize={12} fontWeight={800} color="text.secondary">
                                        These pictures will be automatically deleted after 30 days
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                checked={isFaceVerificationEnabled === "YES"}
                                onChange={(e) => {
                                    const newVal = e.target.checked ? "YES" : "NO";
                                    setIsFaceVerificationEnabled(newVal);
                                    handleUpdateFaceVerification(newVal);
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>
                {/* Holidays Card */}
                <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #cfcbd6", borderRadius: "10px", ml: 1, overflow: "hidden" }}>
                        {/* Header */}
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, cursor: "pointer", userSelect: "none" }}
                            onClick={() => setHolidaysExpanded((prev) => !prev)}
                        >
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#fff3e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                                    🎉
                                </Box>
                                <Box>
                                    <Typography fontWeight={600} fontSize={15}>Holidays</Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        {holidays?.length ?? 0} holidays added for {year}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                                <TextField
                                    name="year"
                                    value={year}
                                    onChange={onChange}
                                    size="small"
                                    select
                                    sx={{ width: 90 }}
                                >
                                    {years?.map((option: any, index: any) => (
                                        <MenuItem key={index} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                                <Tooltip title="Announcement">
                                    <IconButton size="small" onClick={() => setOpenInfo(true)}>
                                        <img src={Announcement} alt="Announcement" style={{ width: 22, height: 22 }} />
                                    </IconButton>
                                </Tooltip>
                                <ExpandMore
                                    onClick={(e) => { e.stopPropagation(); setHolidaysExpanded((prev) => !prev); }}
                                    sx={{ color: "text.secondary", transform: holidaysExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", cursor: "pointer" }}
                                />
                            </Box>
                        </Box>

                        {/* Expanded body */}
                        {holidaysExpanded && (
                            <Box>
                                {holidays?.length ? (
                                    holidays.map((row: any, index: number) => (
                                        <Box key={index}>
                                            <Divider />
                                            <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.25}>
                                                <Typography fontSize={13}>
                                                    {row.holiday} · {moment(row.date).format("DD MMM")}
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleDeleteHoliday(row)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    ))
                                ) : (
                                    <Box>
                                        <Divider />
                                        <Box py={3} textAlign="center">
                                            <Typography color="text.secondary" fontSize={13}>No holidays added for {year}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                <Divider />
                                <Box px={2} py={1.25}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Add />}
                                        onClick={() => setAddOpenHoliday(true)}
                                        sx={{
                                            borderRadius: "20px",
                                            textTransform: "none",
                                            fontWeight: 500,
                                            px: 2,
                                        }}
                                    >
                                        Add holiday
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>

                {/* Geo Fence Card */}
                <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #cfcbd6", borderRadius: "10px", ml: 1, overflow: "hidden" }}>
                        {/* Header */}
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, cursor: "pointer", userSelect: "none" }}
                            onClick={() => setGeoFenceExpanded((prev) => !prev)}
                        >
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <LocationOn sx={{ color: "#388e3c", fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography fontWeight={600} fontSize={15}>Geo Fence</Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        {geoPreferences.locations.filter(l => l.name).length} location(s) · {geoPreferences.enabled ? "Enabled" : "Disabled"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                                {/* Enable switch + default radius */}
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={geoPreferences.enabled}
                                                onChange={(e) => {
                                                    const enabled = e.target.checked;
                                                    setGeoPreferences((prev) => ({ ...prev, enabled }));
                                                    autoSaveGeoFence({ enabled });
                                                }}
                                            />
                                        }
                                        label="Enable Geo Fence"
                                    />
                                    <TextField
                                        label="Default Radius (m)"
                                        size="small"
                                        type="number"
                                        value={geoPreferences.radiusMeters}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setGeoPreferences((prev) => ({ ...prev, radiusMeters: val }));
                                            setDefaultRadiusError("");
                                            if (radiusSaveTimerRef.current) clearTimeout(radiusSaveTimerRef.current);
                                            radiusSaveTimerRef.current = setTimeout(() => autoSaveGeoFence({ radiusMeters: val }), 800);
                                        }}
                                        InputProps={{ inputProps: { min: 1 } }}
                                        error={Boolean(defaultRadiusError)}
                                        helperText={defaultRadiusError}
                                        sx={{ width: 200 }}
                                    />
                                </Box>
                                <Tooltip title="Info">
                                    <IconButton size="small" onClick={() => setGeoOpenInfo(true)}>
                                        <img src={Announcement} alt="Info" style={{ width: 22, height: 22 }} />
                                    </IconButton>
                                </Tooltip>
                                <ExpandMore
                                    onClick={(e) => { e.stopPropagation(); setGeoFenceExpanded((prev) => !prev); }}
                                    sx={{ color: "text.secondary", transform: geoFenceExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", cursor: "pointer" }}
                                />
                            </Box>
                        </Box>

                        {/* Expanded body */}
                        {geoFenceExpanded && (
                            <Box px={2} pb={2} display="flex" flexDirection="column" gap={2}>
                                <Divider />
                                

                                {/* Locations */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle2" fontWeight={600}>Locations</Typography>
                                    <Button size="small" startIcon={<Add />} onClick={handleAddGeoLocation}>Add</Button>
                                </Box>

                                <Box display="flex" flexDirection="column" gap={1}>
                                    {geoPreferences.locations.map((loc, index) => (
                                        <Paper key={index} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                                            <Grid container spacing={1.25} alignItems="flex-start">
                                                <Grid item xs={12} md={1.1}>
                                                    <Typography variant="subtitle2" sx={{ height: 40, display: "flex", alignItems: "center" }}>
                                                        Location {index + 1}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField label="Location Name *" size="small" value={loc.name}
                                                        onChange={(e) => updateGeoLocation(index, "name", e.target.value)}
                                                        error={Boolean(locationErrors[index]?.name)}
                                                        helperText={getGeoFenceHelperText(locationErrors[index]?.name)} fullWidth />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <TextField label="Latitude *" size="small" type="number" value={loc.latitude}
                                                        onChange={(e) => updateGeoLocation(index, "latitude", e.target.value)}
                                                        error={Boolean(locationErrors[index]?.latitude)}
                                                        helperText={getGeoFenceHelperText(locationErrors[index]?.latitude)} fullWidth />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <TextField label="Longitude *" size="small" type="number" value={loc.longitude}
                                                        onChange={(e) => updateGeoLocation(index, "longitude", e.target.value)}
                                                        error={Boolean(locationErrors[index]?.longitude)}
                                                        helperText={getGeoFenceHelperText(locationErrors[index]?.longitude)} fullWidth />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <TextField label="Radius (m)" size="small" type="number" value={loc.radiusMeters}
                                                        onChange={(e) => updateGeoLocation(index, "radiusMeters", e.target.value)}
                                                        InputProps={{ inputProps: { min: 1 } }}
                                                        placeholder={geoPreferences.radiusMeters}
                                                        error={Boolean(locationErrors[index]?.radiusMeters)}
                                                        helperText={locationErrors[index]?.radiusMeters} fullWidth />
                                                </Grid>
                                                <Grid item xs={12} md={1.9}>
                                                    <Box sx={{ height: 40, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                                                        {fetchingLocationIndex === index && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>Fetching...</Typography>
                                                        )}
                                                        <Tooltip title="Use current location">
                                                            <IconButton size="small" disabled={fetchingLocationIndex !== null} onClick={() => handleUseCurrentGeoLocation(index)}>
                                                                <MyLocationIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete location">
                                                            <IconButton size="small" onClick={() => handleRemoveGeoLocation(index)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    ))}
                                </Box>

                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleSaveGeoFence}
                                        disabled={isSavingGeoFence}
                                        sx={{
                                            borderRadius: "20px",
                                            textTransform: "none",
                                            fontWeight: 500,
                                            px: 2.5,
                                        }}
                                    >
                                        {isSavingGeoFence ? "Saving..." : "Save Geo Fence"}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>

                {/* Work Shifts section */}

                <Grid item xs={12}>
                    <Box sx={{ border: "1px solid #cfcbd6", borderRadius: "10px", ml: 1, overflow: "hidden" }}>
                        {/* Header */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#e3f2fd", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Work sx={{ color: "#1565c0", fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <Typography fontWeight={600} fontSize={15}>Work Shifts</Typography>
                                        {mismatchedShifts.length > 0 && (
                                            <Tooltip
                                                title={
                                                    <Box>
                                                        <Typography fontSize={12} fontWeight={700} mb={0.5}>
                                                            Doesn't match Standard Working Hours ({formatMinutesDuration(stdMinutesTotal!)}{stdSchedule?.scheduled ? ` from ${moment(stdSchedule.scheduled.effectiveDate).format("DD-MM-YYYY")}` : ""})
                                                        </Typography>
                                                        {mismatchedShifts.map((s) => (
                                                            <Typography key={s.id} fontSize={12}>
                                                                • {s.name}: {formatMinutesDuration(getShiftDurationMinutes(s))}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                }
                                            >
                                                <WarningAmberOutlined sx={{ color: "#ed6c02", fontSize: 18 }} />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Typography fontSize={12} color="text.secondary">
                                        One shift is the default for everyone. Assign a different shift to specific people — like a Night Shift for your US client team.
                                    </Typography>
                                </Box>
                            </Box>
                            {shifts.length > 1 && (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setShowAllShifts((p) => !p)}
                                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: "#1565c0", whiteSpace: "nowrap", ml: 1 }}
                                >
                                    {showAllShifts ? "Show less" : `Show all (${shifts.length})`}
                                </Button>
                            )}
                        </Box>

                        {/* Standard Working Hours */}
                        <Box sx={{ px: 2.5, pb: 1.5 }}>

                            {!isEditingStdHours ? (
                                <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: "#f5f7fa", borderRadius: "8px", px: 1.5, py: 1, width: "fit-content" }}>
                                    <AccessTime sx={{ fontSize: 16, color: "#388e3c" }} />
                                    <Typography fontSize={13} color="text.secondary">Standard Working Hours:</Typography>
                                    <Typography fontSize={13} fontWeight={700} color="text.primary">
                                        {stdSchedule ? formatMinutesDuration(stdSchedule.currentMinutes) : fmtDuration(hours, minutes)}
                                    </Typography>
                                    {stdSchedule?.scheduled && (
                                        <Tooltip title="Saved today - applies from this date. Earlier attendance keeps the current value.">
                                            <Typography fontSize={12} fontWeight={600} sx={{ color: "#ed6c02", bgcolor: "#fff4e5", borderRadius: "6px", px: 1, py: 0.25 }}>
                                                {formatMinutesDuration(stdSchedule.scheduled.minutes)} from {moment(stdSchedule.scheduled.effectiveDate).format("DD-MM-YYYY")}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Edit standard hours">
                                        <IconButton size="small" onClick={() => setIsEditingStdHours(true)} sx={{ ml: 0.5 }}>
                                            <EditOutlined sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ) : (
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ bgcolor: "#f5f7fa", borderRadius: "8px", px: 1.5, py: 1 }}>
                                    <AccessTime sx={{ fontSize: 16, color: "#388e3c" }} />
                                    <Typography fontSize={13} color="text.secondary" whiteSpace="nowrap">Standard Working Hours:</Typography>
                                    <Autocomplete
                                        size="small"
                                        options={getHoursOptionsForSWH()}
                                        getOptionLabel={(o) => o.label}
                                        value={hours}
                                        onChange={(_, v) => setHours(v)}
                                        renderInput={(p) => <TextField {...p} label="Hours" />}
                                        sx={{ width: 100 }}
                                    />
                                    <Autocomplete
                                        size="small"
                                        options={getMinutesOptions()}
                                        getOptionLabel={(o) => o.label}
                                        value={minutes}
                                        onChange={(_, v) => setMinutes(v)}
                                        renderInput={(p) => <TextField {...p} label="Mins" />}
                                        sx={{ width: 90 }}
                                    />
                                    <Button size="small" variant="contained" onClick={handleSaveStdHours} disabled={insertLoad}
                                        sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "#1a2e4a", "&:hover": { bgcolor: "#263d5c" }, px: 2 }}>
                                        Save
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => setIsEditingStdHours(false)}
                                        sx={{ textTransform: "none", borderRadius: "8px", px: 1.5 }}>
                                        Cancel
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {/* Shift list */}
                        {isShiftsLoading ? (
                            [1, 2].map((i) => (
                                <Box key={i}>
                                    <Divider />
                                    <Box display="flex" alignItems="center" gap={2} px={2.5} py={1.5}>
                                        <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 2, flexShrink: 0 }} />
                                        <Box flex={1}>
                                            <Skeleton variant="text" width="40%" height={20} />
                                            <Skeleton variant="text" width="65%" height={16} sx={{ mt: 0.5 }} />
                                        </Box>
                                        <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: 1 }} />
                                    </Box>
                                </Box>
                            ))
                        ) : shifts.length > 0 ? (
                            ([...shifts]
                                .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                                .slice(0, showAllShifts ? undefined : 1)
                            ).map((shift: any, index: number) => (
                                <Box key={shift.id ?? index}>
                                    <Divider />
                                    <Box display="flex" alignItems="center" gap={2} px={2.5} py={1.5}>
                                        {(() => { const { emoji, bg } = getShiftIcon(shift); return (
                                            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                                                {emoji}
                                            </Box>
                                        ); })()}
                                        <Box flex={1}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontWeight={700} fontSize={14}>{shift.name}</Typography>
                                                {shift.isDefault && (
                                                    <Box sx={{
                                                        display: "inline-flex", alignItems: "center",
                                                        bgcolor: "#e8f5e9", color: "#2e7d32",
                                                        border: "1px solid #a5d6a7",
                                                        borderRadius: "20px", fontSize: 10,
                                                        fontWeight: 700, px: 1.2, py: 0.3,
                                                        letterSpacing: 0.8, lineHeight: 1.4,
                                                    }}>
                                                        ✓ Default
                                                    </Box>
                                                )}
                                                {stdMinutesTotal != null && getShiftDurationMinutes(shift) !== stdMinutesTotal && (
                                                    <Tooltip
                                                        title={`This shift is ${formatMinutesDuration(getShiftDurationMinutes(shift))} long — doesn't match Standard Working Hours (${formatMinutesDuration(stdMinutesTotal)}). Edit this shift to fix it.`}
                                                    >
                                                        <WarningAmberOutlined sx={{ color: "#ed6c02", fontSize: 16 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Typography fontSize={12} color="text.secondary">
                                                {shift.inTime} – {shift.firstHalfOutTime || shift.outTime} · {shift.secondHalfInTime || shift.inTime} – {shift.outTime}
                                                {shift.tagline ? ` · ${shift.tagline}` : ""}
                                            </Typography>
                                            {(shift.users?.length > 0) && (
                                                <Typography fontSize={11} color="text.secondary" mt={0.3}>
                                                    {shift.users.length} {shift.users.length === 1 ? "person" : "people"} assigned
                                                </Typography>
                                            )}
                                        </Box>
                                        {!shift.isDefault && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={isSettingDefault}
                                                onClick={() => setDefaultShift(shift.id)}
                                                sx={{ borderRadius: "20px", textTransform: "none", fontSize: 11, fontWeight: 600, px: 1.5, py: 0.3, whiteSpace: "nowrap" }}
                                            >
                                                Set default
                                            </Button>
                                        )}
                                        <Tooltip title="Assign users">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (assigningShiftId === shift.id) {
                                                        setAssigningShiftId(null);
                                                    } else {
                                                        setAssigningShiftId(shift.id);
                                                        setAssignUsers(
                                                            orgUsers.filter((u) =>
                                                                (shift.users || []).some((su: any) => su.id === u.id)
                                                            )
                                                        );
                                                    }
                                                }}
                                                sx={{ color: assigningShiftId === shift.id ? "#1565c0" : "text.secondary" }}
                                            >
                                                <PeopleOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <IconButton
                                            size="small"
                                            onClick={() => { setEditingShift(shift); setShiftDialogOpen(true); }}
                                            sx={{ color: "text.secondary" }}
                                        >
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {/* Assign users panel */}
                                    {assigningShiftId === shift.id && (
                                        <Box px={2.5} pb={2} pt={0.5}>
                                            <Divider sx={{ mb: 1.5 }} />
                                            <Typography fontSize={12} fontWeight={600} color="text.secondary" mb={1}>
                                                Assign users to this shift
                                            </Typography>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                options={orgUsers}
                                                getOptionLabel={(u: any) => u.fullName || ""}
                                                value={assignUsers}
                                                onChange={(_, v) => setAssignUsers(v)}
                                                isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                                                renderInput={(p) => <TextField {...p} placeholder="Search and select users..." />}
                                                sx={{ mb: 1.5 }}
                                            />
                                            <Box display="flex" gap={1} justifyContent="flex-end">
                                                <Button size="small" variant="outlined" onClick={() => setAssigningShiftId(null)}
                                                    sx={{ textTransform: "none", borderRadius: "8px" }}>
                                                    Cancel
                                                </Button>
                                                <Button size="small" variant="contained" disabled={isSavingShiftUsers}
                                                    onClick={() => saveShiftUsers({ id: shift.id, userIds: assignUsers.map((u: any) => u.id) })}
                                                    sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "#1a2e4a", "&:hover": { bgcolor: "#263d5c" } }}>
                                                    {isSavingShiftUsers ? "Saving..." : "Save"}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ))
                        ) : (
                            <Box>
                                <Divider />
                                <Box py={3} textAlign="center">
                                    <Typography fontSize={13} color="text.secondary">No shifts added yet</Typography>
                                </Box>
                            </Box>
                        )}


                    </Box>
                </Grid>
            </Grid>
            <GeoFenceInfo open={geoOpenInfo} setOpen={setGeoOpenInfo} />
            <AddHoliday open={AddOpenHoliday} setOpen={setAddOpenHoliday} />
            <ShiftDialog
                open={shiftDialogOpen}
                setOpen={(v) => { setShiftDialogOpen(v); if (!v) setEditingShift(null); }}
                data={editingShift}
                stdHours={hours}
                stdMinutes={minutes}
            />
            <AttendanceInfo open={openInfo} setOpen={setOpenInfo} />
        </Box>
    )
}


const Actions = ({ data, setTotalHolidays, setHolidays, year }) => {
    const confirm = useConfirm();

    const { mutateAsync } = useMutation(deleteHoliday, {
        onSuccess: (res: any) => {
            setTotalHolidays(res?.data?.holidayPreferences?.addholiday);
            const Yearholidays = res?.data?.holidayPreferences?.addholiday.filter((item: any) => moment(item?.date).format("YYYY") === String(year))
            setHolidays(Yearholidays);
            snack.success("Holiday Deleted");
        },
        onError: (err: any) => {
            snack.error(err.response.data.message);
        },
    });

    const handleDelete = () => {
        confirm({
            msg: "Are you sure you want to delete this Holiday?",
            action: () => mutateAsync(data),
        });
    };

    return (
        <Box>
            <Box display="flex" gap={1}>
                <IconButton size="small" onClick={handleDelete}>
                    <Delete />
                </IconButton>
            </Box>
        </Box>
    );
};



const GeoFenceInfo = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
    const points = [
        "Default radius applies to all locations that do not have their own radius.",
        "Add a separate location for each branch, office, or allowed workplace.",
        "Do not add duplicate locations for the same address.",
        "Use the current location button while physically present at the office for better accuracy.",
        "Update coordinates immediately if the office relocates.",
        "Turn Enable Geo Fence on to activate location-based attendance.",
        "If Enable Geo Fence is off, employees can mark attendance from anywhere.",
    ];

    return (
        <DialogWrapper
            title="Important Information on Geo Fence"
            open={open}
            setOpen={setOpen}
            width="md"
        >
            <Box sx={{ padding: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
                    {points.map((point) => (
                        <Typography component="li" variant="body2" key={point} sx={{ mb: 0.75 }}>
                            {point}
                        </Typography>
                    ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Please avoid manually copying coordinates from Google Maps. Go to the actual location and use the current location button to capture the most accurate coordinates.
                </Typography>
            </Box>
        </DialogWrapper>
    )
}


export default AttendancePreferences
